/**
 * How an interval is *written on a button*, which is not how it is stored.
 *
 * The stored and transmitted spelling is one vocabulary — `15mi` minute, `1m`
 * month — chosen so that no two units differ by case and nothing in the stack
 * can turn one into the other. That is a data decision and it is not up for
 * negotiation by a renderer.
 *
 * It is also not what a trader reads. Every chart tool on the planet writes
 * fifteen minutes as `15m`, and a toolbar that says `15mi` looks like a typo of
 * one. So the button shows `15m` and `1M` while the value behind it stays
 * `15mi` and `1m`.
 *
 * **This is presentation only, and one-directional on purpose.** There is no
 * `fromLabel` here and there should not be: a parser for the display form would
 * reintroduce exactly the `m`-means-two-things ambiguity the value spelling
 * exists to remove, and it would sit on the path where a user's typing arrives.
 * Input goes through `interval-mru.ts`'s `canonicalInterval`, which accepts any
 * casing of the real vocabulary and nothing else.
 *
 * Pure and tested here rather than inlined in the picker, for `chart-window.ts`'s
 * reason: anything reaching the SciChart surface is out of jsdom's reach, and
 * this is the part with a table in it.
 */

import type { Frequency } from "@qufolio/model";
import { splitInterval, type IntervalUnit } from "./interval-mru";

/**
 * Unit -> how it is written to a person.
 *
 * Only minute and month differ from the stored spelling, and they are the pair
 * the whole change was about: displaying them the conventional way is the only
 * thing this table buys, so it is a table rather than a transform.
 */
const DISPLAY_UNIT: Record<IntervalUnit, string> = {
  s: "s",
  mi: "m",
  h: "H",
  d: "D",
  w: "W",
  m: "M",
};

/** `15mi` -> `15m`; `1m` -> `1M`; `4h` -> `4H`. */
export function intervalLabel(frequency: Frequency): string {
  const { count, unit } = splitInterval(frequency);
  return `${count}${DISPLAY_UNIT[unit]}`;
}

/**
 * Unit -> its name in words, singular and plural — the command search's rows.
 *
 * `Interval — 45 min` beside `Interval — 45 months` is how a typed `45m` shows
 * both of its readings without either letter doing the explaining.
 */
const UNIT_NAME: Record<IntervalUnit, readonly [string, string]> = {
  s: ["sec", "sec"],
  mi: ["min", "min"],
  h: ["hr", "hr"],
  d: ["day", "days"],
  w: ["week", "weeks"],
  m: ["month", "months"],
};

/** `15mi` -> `15 min`; `1w` -> `1 week`; `3m` -> `3 months`. */
export function intervalName(frequency: Frequency): string {
  const { count, unit } = splitInterval(frequency);
  const [one, many] = UNIT_NAME[unit];
  return `${count} ${count === 1 ? one : many}`;
}

/** Just the unit, for the picker's first slot which shows a unit alone. */
export function unitLabel(unit: IntervalUnit): string {
  return DISPLAY_UNIT[unit];
}
